import { clsx } from "clsx";
import type { KeyboardEvent } from "react";
import { useCallback, useMemo, useRef } from "react";
import {
  addMonths,
  firstOfMonth,
  formatIsoDate,
  inMonth,
  lastOfMonth,
  monthOf,
} from "../../utils/date";
import { useCalendarContext } from "./context";
import { CalendarView } from "./types";
import { useFocusRequest } from "./useFocusRequest";

const COLUMNS = 2;

/** Month steps per key; Home and End reach the year's ends. */
const offsetOf = (key: string, month: number): number | undefined => {
  const steps: Record<string, number> = {
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -COLUMNS,
    ArrowDown: COLUMNS,
    Home: 1 - month,
    End: 12 - month,
    PageUp: -12,
    PageDown: 12,
  };
  return steps[key];
};

export const Months = () => {
  const { value, today, first, last, locale, cursor, moveTo, showView } =
    useCalendarContext();
  const gridRef = useRef<HTMLDivElement>(null);
  useFocusRequest(gridRef);

  const { year, month: cursorMonth } = monthOf(cursor);

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const month = { year, month: i + 1 };
        return {
          month: i + 1,
          name: formatIsoDate(firstOfMonth(month), locale.formats.month),
          disabled: lastOfMonth(month) < first || firstOfMonth(month) > last,
        };
      }),
    [year, first, last, locale],
  );

  const choose = useCallback(
    (month: number) => {
      moveTo(inMonth(cursor, { year, month }), true);
      showView(CalendarView.Days, true);
    },
    [cursor, year, moveTo, showView],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        // Back to the days; the popup a calendar sits in stays open.
        e.stopPropagation();
        showView(CalendarView.Days, true);
        return;
      }
      const offset = offsetOf(e.key, cursorMonth);
      if (offset === undefined) return;
      e.preventDefault();
      moveTo(addMonths(cursor, offset), true);
    },
    [cursor, cursorMonth, moveTo, showView],
  );

  const selected = value === null ? undefined : monthOf(value);
  const current = monthOf(today);

  return (
    <div className="nuco-calendar__months-wrapper">
      <div
        ref={gridRef}
        className="nuco-calendar__dates nuco-text nuco-text--md nuco-text--medium"
        onKeyDown={onKeyDown}
      >
        {months.map(({ month, name, disabled }) => (
          <button
            type="button"
            key={month}
            className={clsx(
              "nuco-calendar__dateButton nuco-calendar__month",
              current.year === year &&
                current.month === month &&
                "nuco-calendar__dateButton--today",
              selected?.year === year &&
                selected.month === month &&
                "nuco-calendar__dateButton--selected",
              disabled && "nuco-calendar__dateButton--disabled",
            )}
            tabIndex={month === cursorMonth ? 0 : -1}
            aria-disabled={disabled || undefined}
            onClick={disabled ? undefined : () => choose(month)}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
};
